import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { ShieldCheck, Plus, Trash2, UserPlus, KeyRound, Loader2, X, CheckCircle2, Pencil, Save, Eye, EyeOff, AlertTriangle, ClipboardCheck, ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import AdminMastersPage from "./AdminMastersPage";

const PERMISSION_META = {
  // CONTRACTORS
  view_contractors: { label: "View Contractors", group: "Contractors" },
  approve_contractors: { label: "Approve Contractors", group: "Contractors" },
  reject_contractors: { label: "Reject Contractors", group: "Contractors" },
  edit_contractors: { label: "Edit Contractors", group: "Contractors" },
  delete_contractors: { label: "Delete Contractors", group: "Contractors" },
  approve_workmen: { label: "Approve Induction — Workmen (Stage 2)", group: "Contractors" },
  // PAYROLL MANPOWER
  view_payroll: { label: "View Payroll Manpower", group: "Payroll Manpower" },
  approve_payroll: { label: "Approve Payroll Manpower", group: "Payroll Manpower" },
  reject_payroll: { label: "Reject Payroll Manpower", group: "Payroll Manpower" },
  approve_payroll_roster: { label: "Approve Induction — Pay Roster (Stage 2)", group: "Payroll Manpower" },
  // REPORTS (per-report toggles — frontend gates each report by these)
  report_manpower_registration: { label: "Manpower Registration Details", group: "Reports" },
  report_contractor_workforce: { label: "Contractor NCC Workforce", group: "Reports" },
  report_payroll_workforce: { label: "Payroll NCC Workforce", group: "Reports" },
  report_new_contractors: { label: "New Contractors (Non-NCC)", group: "Reports" },
  report_new_payroll: { label: "New Payroll Manpower (Non-NCC)", group: "Reports" },
  report_ready_mobilize: { label: "Ready to Mobilize / Join", group: "Reports" },
  report_ncc_sites: { label: "NCC Sites", group: "Reports" },
  report_workforce: { label: "Workforce", group: "Reports" },
  report_funnel: { label: "Registration Funnel", group: "Reports" },
  report_directory: { label: "Directory", group: "Reports" },
  report_statutory: { label: "Statutory Compliance", group: "Reports" },
  // WORKMEN ROSTER — links to Status → Workmen Roster + Reports → Workmen Roster
  workmen_roster_approval: { label: "Roster Approval (Status · Workmen Roster)", group: "Workmen Roster" },
  workmen_roster_report: { label: "Roster Report (Reports · Workmen Roster)", group: "Workmen Roster" },
  approve_medical: { label: "Approve Medical — Workmen & Pay Roster (Stage 1)", group: "Workmen Roster" },
  // SYSTEM
  manage_roles: { label: "Manage Roles", group: "System" },
  manage_admins: { label: "Manage Admins", group: "System" },
  // OTHERS
  manage_masters: { label: "Manage Masters", group: "Others" },
  // DASHBOARD
  view_stats: { label: "View Stats / Dashboard", group: "Dashboard" },
};

// Render order for permission groups inside the Create/Edit Role modal.
const GROUP_ORDER = ["Contractors", "Payroll Manpower", "Reports", "Workmen Roster", "System", "Others", "Dashboard"];

export default function AdminSettingsPage() {
  const { user, loading, can } = useAuth();
  // Default to Masters tab as user requested it before Roles
  const [tab, setTab] = useState(can("manage_masters") || (user && user.is_super) ? "masters" : "roles");

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!can("manage_roles", "manage_admins", "manage_masters")) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <div className="border-2 border-slate-900 bg-white p-8 shadow-brutal">
          <ShieldCheck className="mx-auto h-10 w-10 text-slate-700" />
          <h1 className="mt-4 font-heading text-2xl font-black uppercase">Access denied</h1>
          <p className="mt-2 text-slate-600">You don&apos;t have permission to view this page.</p>
          <Link to="/admin" className="mt-5 inline-block border-2 border-slate-900 bg-ncc-gold px-4 py-2 industrial-label">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  const tabs = [];
  if (user.is_super || can("manage_masters")) tabs.push({ id: "masters", label: "Masters" });
  if (user.is_super || can("manage_roles")) tabs.push({ id: "roles", label: "Roles" });
  if (user.is_super || can("manage_admins")) tabs.push({ id: "admins", label: "Admin Users" });

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8">
      <div className="industrial-label text-ncc-gold2">// Admin · Settings</div>
      <h1 className="mt-2 font-heading text-3xl font-black uppercase tracking-tight text-slate-900 md:text-5xl">
        {tab === "masters" ? "Data Masters" : tab === "admins" ? "Admin Users" : "Roles & Access"}
      </h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-600">
        {tab === "masters"
          ? "Manage the lookup lists used across registration, search, and admin. Changes apply instantly across the app."
          : tab === "admins"
          ? "Invite, edit, and remove admin team members. Assign their role, the NCC sites they can see, and check who can approve each queue."
          : "Create custom roles with granular permissions and invite team members."}
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-1 border-b-2 border-slate-900">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`settings-tab-${t.id}`}
            className={`px-4 py-2 industrial-label transition-colors ${tab === t.id ? "bg-ncc-navy text-white" : "text-slate-700 hover:bg-slate-100"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "masters" && <AdminMastersPage embedded />}
        {tab === "roles" && <RolesTab can={can} />}
        {tab === "admins" && <AdminsTab can={can} />}
      </div>
    </div>
  );
}

/* ---------------- Roles & Access (redesigned — left sidebar + per-page H/RO/FA) ---------------- */
// Map each "page" the app exposes to a triplet of permission keys. A role's
// access level for a page is derived from which of these keys are in
// role.permissions:
//   • Hidden       = view keys absent
//   • Read-only    = all view keys present, but at least one extras key missing
//   • Full Access  = all view + extras keys present
// Switching levels in the UI adds/removes those exact keys from the role.
const PAGE_DEFS = [
  { section: "DASHBOARD", pages: [
    { id: "ncc-swms-command-center", label: "NCC-SWMS Command Center", view: ["view_stats"], extras: [] },
    { id: "executive-dashboard", label: "Executive Dashboard", view: ["view_stats"], extras: [] },
  ]},
  { section: "CONTRACTORS", pages: [
    { id: "register-contractors", label: "Register / Manage Contractors", view: ["view_contractors"], extras: ["edit_contractors", "delete_contractors"] },
    { id: "approval-contractors", label: "Approval — Contractors", view: ["view_contractors"], extras: ["approve_contractors", "reject_contractors"] },
  ]},
  { section: "PAYROLL MANPOWER", pages: [
    { id: "approval-payroll", label: "Approval — Payroll", view: ["view_payroll"], extras: ["approve_payroll", "reject_payroll"] },
    { id: "approval-pay-roster", label: "Approval — Pay Roster · Induction (Stage 2)", view: ["view_payroll"], extras: ["approve_payroll_roster"] },
  ]},
  { section: "WORKMEN ROSTER", pages: [
    { id: "status-workmen-roster", label: "Status — Workmen Roster", view: ["workmen_roster_approval"], extras: [] },
    { id: "approval-medical", label: "Approval — Medical · Stage 1 (Workmen & Pay Roster)", view: ["workmen_roster_approval"], extras: ["approve_medical"] },
    { id: "approval-workmen", label: "Approval — Workmen · Induction (Stage 2)", view: ["workmen_roster_approval"], extras: ["approve_workmen"] },
  ]},
  { section: "REPORTS", pages: [
    { id: "report-directory", label: "Report Directory", view: ["report_directory"], extras: [] },
    { id: "report-manpower-registration", label: "Manpower Registration Details", view: ["report_manpower_registration"], extras: [] },
    { id: "report-contractor-workforce", label: "Contractor Workforce", view: ["report_contractor_workforce"], extras: [] },
    { id: "report-payroll-workforce", label: "Payroll Workforce", view: ["report_payroll_workforce"], extras: [] },
    { id: "report-new-contractors", label: "New Contractors", view: ["report_new_contractors"], extras: [] },
    { id: "report-new-payroll", label: "New Payroll", view: ["report_new_payroll"], extras: [] },
    { id: "report-ready-mobilize", label: "Ready-to-Mobilize", view: ["report_ready_mobilize"], extras: [] },
    { id: "report-ncc-sites", label: "NCC Sites", view: ["report_ncc_sites"], extras: [] },
    { id: "report-workforce", label: "Workforce", view: ["report_workforce"], extras: [] },
    { id: "report-funnel", label: "Funnel", view: ["report_funnel"], extras: [] },
    { id: "report-workmen-pay-roster", label: "Workmen-Pay Roster", view: ["workmen_roster_report"], extras: [] },
  ]},
  { section: "SYSTEM", pages: [
    { id: "manage-masters", label: "Manage Masters", view: ["manage_masters"], extras: [] },
    { id: "manage-roles", label: "Manage Roles", view: ["manage_roles"], extras: [] },
    { id: "manage-admins", label: "Manage Admin Users", view: ["manage_admins"], extras: [] },
  ]},
];

function levelForPage(rolePerms, page) {
  const set = new Set(rolePerms || []);
  const hasAllView = page.view.every((k) => set.has(k));
  if (!hasAllView) return "hidden";
  const hasAllExtras = page.extras.every((k) => set.has(k));
  if (page.extras.length === 0 || hasAllExtras) return "full";
  return "ro";
}

function setLevelForPage(rolePerms, page, level) {
  const set = new Set(rolePerms || []);
  const all = [...page.view, ...page.extras];
  all.forEach((k) => set.delete(k));
  if (level === "ro") page.view.forEach((k) => set.add(k));
  if (level === "full") all.forEach((k) => set.add(k));
  return [...set];
}

function RolesTab({ can }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [draftPerms, setDraftPerms] = useState([]);
  const [draftName, setDraftName] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newRoleOpen, setNewRoleOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [openSections, setOpenSections] = useState({});

  async function load(preferId) {
    setLoading(true);
    try {
      const r = await api.get("/roles");
      setRoles(r.data);
      const target = (preferId && r.data.find((x) => x.id === preferId)) || r.data[0];
      if (target) selectRole(target);
    } finally { setLoading(false); }
  }
  useEffect(() => { const t = setTimeout(() => load(), 0); return () => clearTimeout(t); }, []);

  function selectRole(r) {
    setSelectedId(r.id);
    setDraftPerms(r.permissions || []);
    setDraftName(r.name || "");
    setDirty(false);
    setError("");
  }
  const selected = useMemo(() => roles.find((r) => r.id === selectedId) || null, [roles, selectedId]);
  const isSystem = !!selected?.is_system;
  const readOnly = isSystem || !can("manage_roles");

  function changeLevel(page, level) {
    if (readOnly) return;
    setDraftPerms((cur) => setLevelForPage(cur, page, level));
    setDirty(true);
  }
  function setSectionAll(section, level) {
    if (readOnly) return;
    setDraftPerms((cur) => {
      let next = cur;
      section.pages.forEach((p) => { next = setLevelForPage(next, p, level); });
      return next;
    });
    setDirty(true);
  }

  async function saveChanges() {
    if (!selected || readOnly) return;
    setSaving(true); setError("");
    try {
      await api.put(`/roles/${selected.id}`, { name: draftName.trim() || selected.name, permissions: draftPerms });
      await load(selected.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  }

  async function createRole() {
    const name = newRoleName.trim();
    if (!name) return;
    try {
      const r = await api.post("/roles", { name, permissions: [] });
      setNewRoleOpen(false);
      setNewRoleName("");
      await load(r.data?.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create role");
    }
  }
  async function deleteRole(r) {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[280px_1fr]" data-testid="roles-access-page">
      {/* LEFT SIDEBAR — role list */}
      <aside className="border-2 border-slate-900 bg-white" data-testid="roles-sidebar">
        <div className="flex items-center justify-between border-b-2 border-slate-900 bg-slate-50 px-3 py-2">
          <div className="industrial-label text-slate-700">{roles.length} role(s)</div>
          {can("manage_roles") && (
            <button type="button" onClick={() => setNewRoleOpen(true)} data-testid="roles-new-btn" className="inline-flex items-center gap-1 border-2 border-slate-900 bg-ncc-gold px-2 py-1 industrial-label text-[11px] hover:-translate-y-0.5 hover:shadow-brutal-sm">
              <Plus className="h-3 w-3" /> New
            </button>
          )}
        </div>
        <ul>
          {loading && <li className="px-3 py-2 font-mono text-xs text-slate-500">Loading…</li>}
          {!loading && roles.length === 0 && <li className="px-3 py-2 font-mono text-xs text-slate-500">No roles yet</li>}
          {roles.map((r) => (
            <li
              key={r.id}
              onClick={() => selectRole(r)}
              data-testid={`roles-item-${r.name}`}
              className={`group flex cursor-pointer items-center gap-2 border-l-4 px-3 py-2 transition-colors ${
                selectedId === r.id ? "border-ncc-gold bg-ncc-tint" : "border-transparent hover:bg-slate-50"
              }`}
            >
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded border-2 border-slate-900 bg-ncc-navy text-ncc-gold">
                <KeyRound className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-heading text-sm font-bold text-slate-900">{r.name}</div>
                <div className="industrial-label text-[10px] text-slate-500">{r.is_system ? "SYSTEM" : `${(r.permissions || []).length} perms`}</div>
              </div>
              {!r.is_system && can("manage_roles") && (
                <button type="button" onClick={(e) => { e.stopPropagation(); deleteRole(r); }} className="hidden text-red-500 hover:text-red-700 group-hover:inline-block" data-testid={`roles-delete-${r.name}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      </aside>

      {/* RIGHT PANE — selected role's per-page access */}
      <section className="border-2 border-slate-900 bg-white" data-testid="role-detail">
        {!selected ? (
          <div className="grid place-items-center py-16 font-mono text-sm text-slate-500">Select a role to view its access</div>
        ) : (
          <>
            <header className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-slate-900 bg-white px-5 py-4">
              <div className="flex items-center gap-2">
                {readOnly ? (
                  <div className="font-heading text-2xl font-black text-slate-900">{selected.name}</div>
                ) : (
                  <input
                    value={draftName}
                    onChange={(e) => { setDraftName(e.target.value); setDirty(true); }}
                    data-testid="role-detail-name"
                    className="border-b-2 border-transparent bg-transparent font-heading text-2xl font-black text-slate-900 focus:border-ncc-navy focus:outline-none"
                  />
                )}
                {isSystem && (
                  <span className="inline-flex items-center gap-1 rounded border-2 border-ncc-gold bg-slate-900 px-2 py-0.5 industrial-label text-[10px] text-ncc-gold">
                    <KeyRound className="h-3 w-3" /> SYSTEM
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {dirty && !readOnly && (
                  <span className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 font-mono text-[11px] text-amber-700">
                    <AlertTriangle className="h-3 w-3" /> Unsaved
                  </span>
                )}
                <button
                  type="button"
                  onClick={saveChanges}
                  disabled={readOnly || !dirty || saving}
                  data-testid="role-save-changes"
                  className="inline-flex items-center gap-2 border-2 border-slate-900 bg-ncc-navy px-4 py-2 industrial-label text-white disabled:opacity-40"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
                </button>
              </div>
            </header>
            <p className="border-b border-slate-200 bg-slate-50 px-5 py-2 font-mono text-xs text-slate-600">
              {isSystem ? "Built-in system role." : "Custom role — assign Hidden / Read-only / Full Access per page."}
            </p>
            {error && <div className="mx-5 mt-4 border-2 border-red-500 bg-red-50 p-2 font-mono text-xs text-red-700">{error}</div>}

            <div className="p-5 space-y-3">
              {PAGE_DEFS.map((section) => {
                const sectionHasExtras = section.pages.some((p) => p.extras.length > 0);
                const secKey = section.section.toLowerCase().replace(/\s+/g, "-");
                const open = !!openSections[secKey];
                let nFull = 0, nRo = 0, nHidden = 0;
                section.pages.forEach((p) => {
                  const l = levelForPage(draftPerms, p);
                  if (l === "full") nFull++; else if (l === "ro") nRo++; else nHidden++;
                });
                return (
                <div key={section.section} className="border-2 border-slate-300" data-testid={`role-section-${secKey}`}>
                  <div
                    className="flex cursor-pointer flex-wrap items-center justify-between gap-2 bg-slate-50 px-3 py-2.5 hover:bg-slate-100"
                    onClick={() => setOpenSections((s) => ({ ...s, [secKey]: !s[secKey] }))}
                    data-testid={`role-section-toggle-${secKey}`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                      <div className="industrial-label text-slate-700">{section.section}</div>
                      <span className="font-mono text-[10px] text-slate-400">{section.pages.length} page{section.pages.length === 1 ? "" : "s"}</span>
                      {nFull > 0 && <span className="rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 industrial-label text-[9px] text-emerald-700" data-testid={`role-section-sum-full-${secKey}`}>{nFull} Full</span>}
                      {nRo > 0 && <span className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 industrial-label text-[9px] text-amber-700">{nRo} Read-only</span>}
                      {nHidden > 0 && <span className="rounded border border-red-300 bg-red-50 px-1.5 py-0.5 industrial-label text-[9px] text-red-700">{nHidden} Hidden</span>}
                    </div>
                    {!readOnly && (
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <span className="font-mono text-[11px] text-slate-500">Set all to:</span>
                        <LvlChip data-testid={`role-setall-${secKey}-hidden`} level="hidden" onClick={() => setSectionAll(section, "hidden")} compact />
                        {sectionHasExtras && (
                          <LvlChip data-testid={`role-setall-${secKey}-ro`} level="ro" onClick={() => setSectionAll(section, "ro")} compact />
                        )}
                        <LvlChip data-testid={`role-setall-${secKey}-full`} level="full" onClick={() => setSectionAll(section, "full")} compact />
                      </div>
                    )}
                  </div>
                  {open && (
                  <div className="overflow-x-auto border-t-2 border-slate-300">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 text-slate-700">
                        <tr>
                          <th className="px-3 py-2 text-left industrial-label">Page</th>
                          <th className="px-3 py-2 text-right industrial-label w-[300px]">Access Level</th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.pages.map((p) => {
                          const lvl = levelForPage(draftPerms, p);
                          // Read-only only makes sense when a page has "extras"
                          // (edit/approve/delete) that can be withheld from a
                          // viewer. View-only pages collapse RO+Full into a
                          // single "Full Access" toggle — anything else makes
                          // the chip un-selectable and confuses users.
                          const hasExtras = p.extras.length > 0;
                          return (
                            <tr key={p.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`role-page-${p.id}`}>
                              <td className="px-3 py-2 font-bold text-slate-900">
                                {p.label}
                                {!hasExtras && (
                                  <span className="ml-2 font-mono text-[10px] font-normal uppercase tracking-wider text-slate-400" title="This page has no edit/approve actions — access is either Hidden or Full">view-only</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-end gap-1.5">
                                  <LvlChip level="hidden" active={lvl === "hidden"} onClick={() => changeLevel(p, "hidden")} data-testid={`role-page-${p.id}-hidden`} disabled={readOnly} />
                                  {hasExtras && (
                                    <LvlChip level="ro" active={lvl === "ro"} onClick={() => changeLevel(p, "ro")} data-testid={`role-page-${p.id}-ro`} disabled={readOnly} />
                                  )}
                                  <LvlChip level="full" active={lvl === "full"} onClick={() => changeLevel(p, "full")} data-testid={`role-page-${p.id}-full`} disabled={readOnly} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      {/* New Role modal */}
      {newRoleOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ncc-navy/70 p-4" onClick={() => setNewRoleOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border-2 border-slate-900 bg-white" data-testid="new-role-modal">
            <div className="flex items-center justify-between border-b-2 border-slate-900 bg-ncc-navy px-5 py-3 text-white">
              <div className="industrial-label">Create Role</div>
              <button type="button" onClick={() => setNewRoleOpen(false)}><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5">
              <label className="block">
                <div className="industrial-label text-slate-600">Role Name</div>
                <input
                  autoFocus
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g. Site Supervisor, Project Co-Ordinator"
                  data-testid="new-role-name"
                  className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 border-t-2 border-slate-900 bg-slate-50 px-5 py-3">
              <button type="button" onClick={() => setNewRoleOpen(false)} className="border-2 border-slate-900 bg-white px-4 py-2 industrial-label">Cancel</button>
              <button type="button" onClick={createRole} data-testid="new-role-create" className="inline-flex items-center gap-2 border-2 border-slate-900 bg-ncc-gold px-4 py-2 industrial-label">
                <Plus className="h-4 w-4" /> Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Pill button used for "Hidden / Read-only / Full Access".
function LvlChip({ level, active = false, onClick, disabled, compact, ...rest }) {
  const label = level === "hidden" ? "Hidden" : level === "ro" ? "Read-only" : "Full Access";
  const Icon = level === "hidden" ? EyeOff : level === "ro" ? Eye : ShieldCheck;
  const tone =
    level === "hidden"
      ? (active ? "bg-red-600 text-white border-red-700" : "bg-red-50 text-red-700 border-red-300 hover:bg-red-100")
      : level === "ro"
      ? (active ? "bg-amber-500 text-white border-amber-600" : "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100")
      : (active ? "bg-emerald-600 text-white border-emerald-700" : "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100");
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1 rounded-md border-2 ${compact ? "px-1.5 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"} industrial-label transition-colors ${tone} disabled:cursor-not-allowed disabled:opacity-50`}
      {...rest}
    >
      <Icon className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} /> {label}
    </button>
  );
}

/* ---------------- Admins ---------------- */
function AdminsTab({ can }) {
  const formatMobile = (m) => {
    if (!m) return "";
    const d = String(m).replace(/\D/g, "");
    if (d.length === 12 && d.startsWith("91")) return `+91 ${d.slice(2, 7)} ${d.slice(7)}`;
    if (d.length === 10) return `${d.slice(0, 5)} ${d.slice(5)}`;
    return d;
  };
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", mobile: "", password: "", role_id: "", name: "", emp_no: "", designation: "", department: "", sites: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [a, r, s] = await Promise.all([api.get("/admins"), api.get("/roles"), api.get("/masters/ncc_site")]);
      setAdmins(a.data);
      setRoles(r.data);
      setSiteOptions((s.data || []).map((m) => m.name).filter(Boolean));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 0);
    return () => clearTimeout(t);
  }, []);

  function openInvite() {
    setEditing(null);
    setForm({ email: "", mobile: "", password: "", role_id: "", name: "", emp_no: "", designation: "", department: "", sites: [] });
    setError(""); setInfo("");
    setShowForm(true);
  }
  function openEdit(a) {
    setEditing(a);
    setForm({
      email: a.email,
      mobile: a.mobile || "",
      password: "",
      role_id: a.role_id,
      name: a.name || "",
      emp_no: a.emp_no || "",
      designation: a.designation || "",
      department: a.department || "",
      sites: Array.isArray(a.sites) ? a.sites : [],
    });
    setError(""); setInfo("");
    setShowForm(true);
  }

  function toggleSite(name) {
    setForm((f) => {
      const has = f.sites.includes(name);
      return { ...f, sites: has ? f.sites.filter((s) => s !== name) : [...f.sites, name] };
    });
  }

  async function save(e) {
    e.preventDefault();
    setError("");
    setInfo("");
    if (!form.role_id) { setError("Please select a role"); return; }
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name,
          role_id: form.role_id,
          email: form.email,
          mobile: form.mobile,
          emp_no: form.emp_no,
          designation: form.designation,
          department: form.department,
          sites: form.sites,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/admins/${editing.id}`, payload);
        setInfo(`Updated ${form.email}`);
      } else {
        await api.post("/admins", form);
        setInfo(`Invited ${form.email}. Welcome email sent.`);
      }
      setForm({ email: "", mobile: "", password: "", role_id: "", name: "", emp_no: "", designation: "", department: "", sites: [] });
      setShowForm(false);
      await load();
      setTimeout(() => setInfo(""), 5000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save admin");
    } finally {
      setSaving(false);
    }
  }

  async function del(a) {
    if (!window.confirm(`Remove admin "${a.email}"?`)) return;
    try {
      await api.delete(`/admins/${a.id}`);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Delete failed");
    }
  }

  function randomPassword() {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$";
    let s = "";
    for (let i = 0; i < 12; i++) s += chars[Math.floor(Math.random() * chars.length)];
    setForm((f) => ({ ...f, password: s }));
  }

  return (
    <div>
      <div className="flex items-end justify-between">
        <div className="industrial-label text-slate-500">{admins.length} admin user(s)</div>
        {can("manage_admins") && (
          <button
            onClick={openInvite}
            data-testid="invite-admin-btn"
            className="inline-flex items-center gap-2 border-2 border-slate-900 bg-ncc-gold px-4 py-2 industrial-label text-slate-900 transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm"
          >
            <UserPlus className="h-4 w-4" /> Invite Admin
          </button>
        )}
      </div>

      {/* Approval Queues overview — merged from the old Approvers tab */}
      <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5" data-testid="approval-queues-strip">
        {APPROVAL_QUEUES.map((q) => {
          const list = admins.filter((a) => a.is_super || (Array.isArray(a.permissions) && a.permissions.includes(q.key)));
          const blocked = !loading && list.length === 0;
          const Icon = q.icon;
          return (
            <Link
              key={q.key}
              to={q.route}
              data-testid={`queue-chip-${q.key}`}
              title={`Open ${q.label} approval queue`}
              className={`flex items-center gap-2.5 border-2 px-3 py-2 transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm ${blocked ? "border-red-500 bg-red-50" : "border-slate-900 bg-white"}`}
            >
              <Icon className={`h-4 w-4 shrink-0 ${blocked ? "text-red-600" : "text-ncc-navy"}`} />
              <div className="min-w-0">
                <div className="truncate industrial-label text-[10px] text-slate-700">{q.label}</div>
                {blocked ? (
                  <div className="flex items-center gap-1 industrial-label text-[10px] text-red-700">
                    <AlertTriangle className="h-3 w-3" /> Blocked — no approvers
                  </div>
                ) : (
                  <div className="font-mono text-[11px] text-slate-500">{loading ? "…" : `${list.length} approver${list.length === 1 ? "" : "s"}`}</div>
                )}
              </div>
            </Link>
          );
        })}
      </div>

      {info && (
        <div className="mt-4 flex items-center gap-2 border-2 border-slate-900 bg-ncc-tint px-4 py-2 font-mono text-sm text-slate-900" data-testid="admin-invite-info">
          <CheckCircle2 className="h-4 w-4 text-ncc-gold2" /> {info}
        </div>
      )}

      <div className="mt-6 overflow-x-auto border-2 border-slate-900 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-ncc-navy text-white">
            <tr>
              {["Site", "Name", "EMP No.", "Designation", "Department", "Email", "Mobile", "Role", "Can Approve", "Created"].map((h) => (
                <th key={h} className="px-4 py-3 text-left industrial-label">{h}</th>
              ))}
              {/* Actions stays pinned to the right edge so the Edit button is
                  always visible — the row has 10 other columns and would
                  otherwise need a horizontal scroll just to reach Edit. */}
              <th className="sticky right-0 z-10 border-l-2 border-ncc-gold bg-ncc-navy px-4 py-3 text-left industrial-label shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.4)]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="p-6 text-center font-mono text-slate-500">Loading…</td></tr>
            ) : admins.length === 0 ? (
              <tr><td colSpan={11} className="p-6 text-center font-mono text-slate-500">No admins</td></tr>
            ) : admins.map((a) => (
              <tr key={a.id} className="group border-b border-slate-200 hover:bg-slate-50" data-testid={`admin-row-${a.email}`}>
                <td className="px-4 py-3 align-top" data-testid={`admin-sites-${a.email}`}>
                  {a.is_super ? (
                    <span className="border border-slate-900 bg-ncc-gold px-1.5 py-0.5 industrial-label text-[10px]">ALL SITES</span>
                  ) : (a.sites && a.sites.length) ? (
                    <div className="flex max-w-[200px] flex-wrap gap-1">
                      {a.sites.slice(0, 3).map((s) => (
                        <span key={s} className="border border-ncc-navy bg-ncc-tint px-1.5 py-0.5 industrial-label text-[10px] text-ncc-navy">{s}</span>
                      ))}
                      {a.sites.length > 3 && <span className="industrial-label text-[10px] text-slate-500">+{a.sites.length - 3}</span>}
                    </div>
                  ) : (
                    <span className="industrial-label text-[10px] text-amber-600">NO SITES</span>
                  )}
                </td>
                <td className="px-4 py-3 font-heading font-bold">{a.name || <span className="font-mono font-normal text-slate-400">—</span>}</td>
                <td className="px-4 py-3 font-mono text-xs" data-testid={`admin-emp-${a.email}`}>{a.emp_no || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 font-mono text-xs" data-testid={`admin-desig-${a.email}`}>{a.designation || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 font-mono text-xs" data-testid={`admin-dept-${a.email}`}>{a.department || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 font-mono">{a.email}</td>
                <td className="px-4 py-3 font-mono">{a.mobile ? formatMobile(a.mobile) : <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3">
                  <span className={`border border-slate-900 px-2 py-0.5 industrial-label ${a.is_super ? "bg-ncc-gold" : "bg-slate-50"}`}>{a.role_name}</span>
                </td>
                <td className="min-w-[170px] px-4 py-3 align-top" data-testid={`admin-approves-${a.email}`}>
                  {(() => {
                    const held = APPROVAL_QUEUES.filter((q) => a.is_super || (Array.isArray(a.permissions) && a.permissions.includes(q.key)));
                    return (
                      <>
                        {held.length === 0 ? (
                          <span className="font-mono text-xs text-slate-400">—</span>
                        ) : (
                          <div className="flex max-w-[220px] flex-wrap gap-1">
                            {held.map((q) => (
                              <span key={q.key} className="whitespace-nowrap border border-emerald-500 bg-emerald-50 px-1.5 py-0.5 industrial-label text-[9px] text-emerald-700">{q.short}</span>
                            ))}
                          </div>
                        )}
                        <div className="mt-1 font-mono text-[10px] text-slate-500">{a.permissions.length} perms</div>
                      </>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                <td className="sticky right-0 z-[5] border-l-2 border-ncc-gold bg-white px-4 py-3 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)] group-hover:bg-slate-50">
                  {can("manage_admins") && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => openEdit(a)}
                        data-testid={`edit-admin-${a.email}`}
                        title="Edit user · change role, sites, designation, password"
                        className="inline-flex items-center gap-1.5 border-2 border-ncc-navy bg-ncc-navy px-3 py-1.5 industrial-label text-[11px] text-white hover:-translate-y-0.5 hover:shadow-brutal-sm"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button
                        onClick={() => del(a)}
                        data-testid={`delete-admin-${a.email}`}
                        title={a.is_super ? "Delete (protected if only Super Admin)" : "Delete"}
                        className="inline-flex items-center gap-1.5 border-2 border-red-500 bg-white px-3 py-1.5 industrial-label text-[11px] text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ncc-navy/70 p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl border-2 border-slate-900 bg-white shadow-brutal-lg" data-testid="invite-admin-form">
            <div className="flex items-center justify-between border-b-2 border-slate-900 bg-ncc-navy px-5 py-3 text-white">
              <div className="industrial-label">{editing ? "Edit Admin" : "Invite Admin"}</div>
              <button type="button" onClick={() => setShowForm(false)}><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-4 p-5">
              <label className="block" data-testid="invite-sites-block">
                <div className="industrial-label text-slate-600">
                  Site (assigned NCC sites)
                  <span className="ml-1 font-mono normal-case text-[10px] text-slate-400">— this user can only access these sites</span>
                </div>
                <div className="mt-1 max-h-48 overflow-y-auto rounded border-2 border-slate-300 bg-slate-50 p-2">
                  {siteOptions.length === 0 ? (
                    <div className="font-mono text-xs text-slate-500">No NCC Sites in masters yet. Add them under Settings → Masters first.</div>
                  ) : (
                    <div className="grid grid-cols-2 gap-1">
                      {siteOptions.map((s) => {
                        const checked = form.sites.includes(s);
                        return (
                          <label
                            key={s}
                            data-testid={`invite-site-opt-${s}`}
                            className={`flex cursor-pointer items-center gap-2 border px-2 py-1 font-mono text-xs ${checked ? "border-ncc-navy bg-ncc-tint text-ncc-navy" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSite(s)}
                              className="h-3.5 w-3.5"
                            />
                            <span className="truncate">{s}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="mt-1 font-mono text-[10px] text-slate-500">
                  {form.sites.length} site{form.sites.length === 1 ? "" : "s"} selected
                </div>
              </label>
              <label className="block">
                <div className="industrial-label text-slate-600">Full Name</div>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={editing ? "Set a display name" : "Optional"}
                  data-testid="invite-name"
                  className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="block">
                  <div className="industrial-label text-slate-600">EMP No.</div>
                  <input
                    value={form.emp_no}
                    onChange={(e) => setForm({ ...form, emp_no: e.target.value })}
                    placeholder="e.g. NCC10245"
                    data-testid="invite-emp-no"
                    className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                  />
                </label>
                <label className="block">
                  <div className="industrial-label text-slate-600">Designation</div>
                  <input
                    value={form.designation}
                    onChange={(e) => setForm({ ...form, designation: e.target.value })}
                    placeholder="e.g. Sr. Engineer"
                    data-testid="invite-designation"
                    className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                  />
                </label>
                <label className="block">
                  <div className="industrial-label text-slate-600">Department</div>
                  <input
                    value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                    placeholder="e.g. HSE / Projects"
                    data-testid="invite-department"
                    className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                  />
                </label>
              </div>
              <label className="block">
                <div className="industrial-label text-slate-600">Email</div>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="invite-email"
                  className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                />
                {editing && <div className="mt-1 font-mono text-xs text-slate-500">Used for sign-in &amp; notifications</div>}
              </label>
              <label className="block">
                <div className="industrial-label text-slate-600">Mobile Number</div>
                <input
                  type="tel"
                  inputMode="tel"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  placeholder="+91 98765 43210"
                  data-testid="invite-mobile"
                  className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                />
                <div className="mt-1 font-mono text-xs text-slate-500">Optional · enables sign-in by mobile</div>
              </label>
              <label className="block">
                <div className="industrial-label text-slate-600">Role</div>
                <select required value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} data-testid="invite-role" className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none">
                  <option value="">— select role —</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="flex items-center justify-between">
                  <div className="industrial-label text-slate-600">{editing ? "New Password (leave blank to keep)" : "Temp Password"}</div>
                  <button type="button" onClick={randomPassword} className="industrial-label text-ncc-gold2 hover:underline" data-testid="random-password-btn">Generate</button>
                </div>
                <input
                  required={!editing}
                  minLength={editing && !form.password ? 0 : 6}
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="invite-password"
                  className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm tracking-wider focus:border-slate-900 focus:outline-none"
                />
                <div className="mt-1 font-mono text-xs text-slate-500">
                  {editing ? "Leave blank to keep existing password" : "Min 6 chars · will be emailed to the user"}
                </div>
              </label>
              {error && <div className="border-2 border-red-500 bg-red-50 p-2 font-mono text-xs text-red-700" data-testid="invite-error">{error}</div>}
            </div>
            <div className="flex shrink-0 items-center justify-end gap-2 border-t-2 border-slate-900 bg-slate-50 px-5 py-3">
              <button type="button" onClick={() => setShowForm(false)} className="border-2 border-slate-900 bg-white px-4 py-2 industrial-label">Cancel</button>
              <button type="submit" disabled={saving} data-testid="invite-submit-btn" className="inline-flex items-center gap-2 border-2 border-slate-900 bg-ncc-gold px-4 py-2 industrial-label">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? <CheckCircle2 className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />)}
                {saving ? "Saving" : (editing ? "Save Changes" : "Invite & Email")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


/* ---------------- Approval Queues (shown inside Admin Users) ---------------- */
// Every approval queue — powers the Approval Queues strip and per-row badges.
const APPROVAL_QUEUES = [
  {
    key: "approve_medical",
    label: "Medical · Stage 1",
    short: "Medical",
    sub: "Stage-1 Medical approval (Workmen & Pay Roster) — required before Induction & ID Card.",
    route: "/admin/workmen-approvals",
    icon: ClipboardCheck,
    accent: "border-rose-500 bg-rose-50 text-rose-800",
  },
  {
    key: "approve_contractors",
    label: "Contractors",
    short: "Contractors",
    sub: "Approve / Reject new Labour & Payroll contractor profiles.",
    route: "/admin/approvals",
    icon: ClipboardCheck,
    accent: "border-emerald-600 bg-emerald-50 text-emerald-800",
  },
  {
    key: "approve_payroll",
    label: "Payroll Manpower",
    short: "Payroll",
    sub: "Approve incoming Payroll Manpower registrations.",
    route: "/admin/payroll-approvals",
    icon: ClipboardCheck,
    accent: "border-blueprint bg-blueprint/10 text-blueprint",
  },
  {
    key: "approve_workmen",
    label: "Workmen Roster · Induction",
    short: "Workmen Induction",
    sub: "Approve workmen added to a contractor's crew.",
    route: "/admin/workmen-approvals",
    icon: ClipboardCheck,
    accent: "border-ncc-navy bg-ncc-tint text-ncc-navy",
  },
  {
    key: "approve_payroll_roster",
    label: "Pay Roster · Induction",
    short: "Pay Roster Induction",
    sub: "Sign-off on Medical / Induction submissions before ID Card issue.",
    route: "/admin/pay-roster-approvals",
    icon: ClipboardCheck,
    accent: "border-amber-500 bg-amber-50 text-amber-800",
  },
];

